/**
 * /accounting/reports/budget-variance — Budgets vs Actuals (single-month view).
 *
 * Edit a monthly budget for any revenue / expense account and watch variance
 * recompute live from posted JEs. Status: on_track / watch (>10%) / breach (>25%).
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, AlertTriangle, CheckCircle2 } from "lucide-react";
import { CockpitPage, CockpitSection } from "@/components/cockpit/CockpitShell";
import { BooksNavRail } from "@/components/accounting/BooksNavRail";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useGeneralLedger } from "@/data/accounting/GeneralLedgerContext";
import { useBudgets, getBudgetVariance, type VarianceStatus } from "@/data/accounting/budgets/BudgetsContext";

const fmt = (n: number) => (n < 0 ? "-" : "") + "$" + Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtPct = (n: number) => (n * 100).toFixed(0) + "%";

const STATUS_BADGE: Record<VarianceStatus, { label: string; className: string }> = {
  on_track: { label: "On track", className: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30" },
  watch:    { label: "Watch",    className: "bg-amber-500/15 text-amber-700 border-amber-500/30" },
  breach:   { label: "Breach",   className: "bg-rose-500/15 text-rose-700 border-rose-500/30" },
};

export default function BudgetVariancePage() {
  const { accounts, journalEntries } = useGeneralLedger();
  const { budgets, setBudget } = useBudgets();

  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [filterType, setFilterType] = useState<"all" | "revenue" | "expense">("all");

  const rows = useMemo(
    () => getBudgetVariance(period, budgets, accounts, journalEntries),
    [period, budgets, accounts, journalEntries],
  );
  const filtered = filterType === "all" ? rows : rows.filter(r => r.type === filterType);

  const totals = useMemo(() => {
    const breach = rows.filter(r => r.status === "breach").length;
    const watch = rows.filter(r => r.status === "watch").length;
    const budgetTotal = rows.reduce((s, r) => s + r.budget, 0);
    const actualTotal = rows.reduce((s, r) => s + r.actual, 0);
    return { breach, watch, budgetTotal, actualTotal };
  }, [rows]);

  return (
    <CockpitPage>
      <div className="mb-3 flex items-center justify-between gap-3">
        <Link to="/accounting" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
          <ArrowLeft className="h-3.5 w-3.5" /> Books
        </Link>
        <Input type="month" value={period} onChange={e => setPeriod(e.target.value)} className="h-8 w-40" />
      </div>
      <BooksNavRail />

      <div className="mb-4">
        <h1 data-tour-id="budget-variance-table" className="text-xl font-medium tracking-tight">Budget vs Actuals</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Edit monthly budgets per account. Variance and status update live from the GL.
        </p>
      </div>

      <CockpitSection>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Card label="Accounts in scope" value={String(rows.length)} />
          <Card label="Total budget" value={fmt(totals.budgetTotal)} />
          <Card label="Total actual" value={fmt(totals.actualTotal)} />
          <Card
            label="Status"
            value={totals.breach > 0 ? `${totals.breach} breach` : totals.watch > 0 ? `${totals.watch} watch` : "All on track"}
            tone={totals.breach > 0 ? "alert" : totals.watch > 0 ? "warn" : "ok"}
            icon={totals.breach > 0 ? <AlertTriangle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
          />
        </div>
      </CockpitSection>

      <CockpitSection
        title="Variance detail"
        actions={
          <div className="flex items-center gap-1.5">
            {(["all", "revenue", "expense"] as const).map(t => (
              <button key={t} onClick={() => setFilterType(t)}
                className={"text-xs px-2.5 py-1 rounded-sm border " +
                  (filterType === t
                    ? "bg-primary/15 text-primary border-primary/30 ring-1 ring-primary/30"
                    : "border-border hover:bg-accent/40")}>
                {t === "all" ? "All" : t === "revenue" ? "Revenue" : "Expense"}
              </button>
            ))}
          </div>
        }
        className="mt-6"
      >
        <div className="rounded-md border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">Account</th>
                <th className="text-right px-3 py-2 w-32">Budget</th>
                <th className="text-right px-3 py-2 w-28">Actual</th>
                <th className="text-right px-3 py-2 w-28">Variance</th>
                <th className="text-right px-3 py-2 w-20">%</th>
                <th className="text-right px-3 py-2 w-28">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={6} className="text-center text-muted-foreground py-8">No revenue or expense activity in {period} yet. Set a budget below to start tracking.</td></tr>
              ) : filtered.map(r => {
                const status = STATUS_BADGE[r.status];
                return (
                  <tr key={r.accountId} className="border-t border-border">
                    <td className="px-3 py-1.5">
                      <span className="font-mono text-xs text-muted-foreground mr-2">{r.accountNumber}</span>
                      {r.accountName}
                    </td>
                    <td className="px-3 py-1 text-right">
                      <Input
                        type="number"
                        defaultValue={r.budget || ""}
                        placeholder="0"
                        className="h-7 text-right tabular-nums"
                        onBlur={e => {
                          const v = Number(e.target.value) || 0;
                          if (v !== r.budget) setBudget(r.accountId, period, v);
                        }}
                      />
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{fmt(r.actual)}</td>
                    <td className={"px-3 py-1.5 text-right tabular-nums " +
                      (r.direction === "unfavorable" ? "text-rose-600" : r.direction === "favorable" ? "text-emerald-600" : "text-muted-foreground")}>
                      {r.variance === 0 ? "—" : fmt(r.variance)}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">{r.budget > 0 ? fmtPct(r.variancePct) : "—"}</td>
                    <td className="px-3 py-1.5 text-right">
                      <Badge variant="outline" className={"text-xs " + status.className}>{status.label}</Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Variance direction is favorable when expenses come in under budget or revenue exceeds budget. Threshold: watch &gt;10% unfavorable • breach &gt;25%.
        </p>
      </CockpitSection>
    </CockpitPage>
  );
}

function Card({ label, value, tone, icon }: {
  label: string; value: string;
  tone?: "ok" | "warn" | "alert"; icon?: React.ReactNode;
}) {
  const toneClass =
    tone === "alert" ? "text-rose-700"
    : tone === "warn" ? "text-amber-600"
    : tone === "ok" ? "text-emerald-600"
    : "text-foreground";
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="text-xs text-muted-foreground inline-flex items-center gap-1.5">{icon}{label}</div>
      <div className={`mt-1 text-xl font-semibold tabular-nums ${toneClass}`}>{value}</div>
    </div>
  );
}
